import React from 'react';
import { motion } from 'motion/react';

import {
  Cloud,
  Server,
  Shield,
  Zap,
  Database,
  Globe,
  Lock,
  RefreshCw,
  CheckCircle,
  ArrowRight,
  HardDrive,
  Network,
  BarChart3,
  Settings,
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SpamProtectedForm from '../../components/SpamProtectedForm';
import '../../styles/services.css';

const features = [
  { icon: Server, title: 'Cloud Migration', desc: 'Seamless migration of your infrastructure to the cloud' },
  { icon: Database, title: 'Cloud Storage', desc: 'Scalable and secure data storage solutions' },
  { icon: Network, title: 'Cloud Networking', desc: 'High-performance network architecture' },
  { icon: Shield, title: 'Cloud Security', desc: 'Enterprise-grade security and compliance' },
  { icon: RefreshCw, title: 'Disaster Recovery', desc: 'Business continuity and backup solutions' },
  { icon: Settings, title: 'Managed Services', desc: '24/7 monitoring and management' },
];

const providers = ['AWS', 'Azure', 'Google Cloud', 'DigitalOcean'];

const CloudServicesPage = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero */}
      <section className="service-hero cloud">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="service-hero-content"
          >
            <span className="service-badge">
              <Cloud size={16} /> Cloud Services
            </span>
            <h1>Elevate Your Infrastructure</h1>
            <p>Modernize your IT infrastructure with our comprehensive cloud solutions. Scale effortlessly, reduce costs, and stay ahead of the competition.</p>
            <div className="hero-buttons">
              <a href="#contact" className="btn-service-primary">
                Get Started <ArrowRight size={18} />
              </a>
              <a href="#features" className="btn-service-secondary">
                Our Services
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="service-features" id="features">
        <div className="container-custom">
          <div className="section-header-service">
            <span className="section-tag">Cloud Solutions</span>
            <h2>Comprehensive Cloud Services</h2>
            <p>End-to-end cloud solutions for modern businesses</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="feature-card"
              >
                <div className="feature-icon cloud">
                  <feature.icon size={24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Providers */}
      <section className="service-providers">
        <div className="container-custom">
          <div className="section-header-service">
            <span className="section-tag">Expertise</span>
            <h2>Multi-Cloud Expertise</h2>
            <p>We work with all major cloud providers</p>
          </div>
          
          <div className="providers-grid">
            {providers.map((provider, index) => (
              <motion.div
                key={provider}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="provider-card"
              >
                <Cloud size={32} />
                <span>{provider}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="service-contact" id="contact">
        <div className="container-custom">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2>Start Your Cloud Journey</h2>
              <p>Let's discuss how cloud solutions can transform your business.</p>
              <ul className="contact-benefits">
                <li><CheckCircle size={20} /> Free cloud assessment</li>
                <li><CheckCircle size={20} /> Cost optimization analysis</li>
                <li><CheckCircle size={20} /> Migration roadmap</li>
                <li><CheckCircle size={20} /> 24/7 support options</li>
              </ul>
            </div>
            
            <SpamProtectedForm 
              emailSubject="Cloud Services Inquiry"
              submitText="Send Message"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CloudServicesPage;
